import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Download, FileText, Loader2, Save, Smartphone, AlertTriangle, Music } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getWhatsAppErrorMessage, whatsappService, WhatsAppChatMessage } from "@/services/whatsapp";
import { MessageAck } from "./MessageAck";
import { cn } from "@/lib/utils";

const SYSTEM_MESSAGE_TYPES = ["e2e_notification", "notification_template"];

type MediaType = "image" | "video" | "audio" | "other";

function getMediaType(mimetype: string | null): MediaType {
  const mime = mimetype || "";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return "other";
}

function thumbToBase64(thumb: unknown): string | null {
  if (!thumb) return null;
  // GOWS/WEBJS: string base64
  if (typeof thumb === "string") return thumb;
  // NOWEB: buffer serializado { type: 'Buffer', data: base64 }
  const buffer = thumb as { type?: string; data?: unknown };
  if (buffer.type === "Buffer" && typeof buffer.data === "string") return buffer.data;
  return null;
}

// Extrae mimetype, nombre y miniatura del payload crudo según el engine de WAHA
function getInitialMedia(message: WhatsAppChatMessage) {
  const result: { mimetype: string | null; filename: string | null; thumbnail: string | null } = {
    mimetype: message.media?.mimetype ?? null,
    filename: message.media?.filename ?? null,
    thumbnail: null,
  };
  if (!message.hasMedia) return result;

  // WEBJS: _data.body es una miniatura JPEG para imagen/video
  const data = message._data;
  if (data?.body && (data.type === "image" || data.type === "video")) {
    result.mimetype = data.mimetype || (data.type === "image" ? "image/jpeg" : "video/mp4");
    result.thumbnail = `data:image/jpeg;base64,${data.body}`;
    return result;
  }

  // GOWS: _data.Message / JPEGThumbnail — NOWEB: _data.message / jpegThumbnail
  const raw = data?.Message || data?.message;
  if (!raw) return result;
  const media = raw.imageMessage || raw.videoMessage || raw.documentMessage || raw.audioMessage;
  if (!media) return result;

  const fallbackMime = raw.imageMessage
    ? "image/jpeg"
    : raw.videoMessage
      ? "video/mp4"
      : raw.audioMessage
        ? "audio/ogg"
        : "application/octet-stream";
  result.mimetype = media.mimetype || fallbackMime;
  if (raw.documentMessage?.fileName) result.filename = raw.documentMessage.fileName;
  const thumb = thumbToBase64(media.JPEGThumbnail || media.jpegThumbnail);
  if (thumb) result.thumbnail = `data:image/jpeg;base64,${thumb}`;
  return result;
}

interface ChatMessageBubbleProps {
  message: WhatsAppChatMessage;
  chatId: string;
}

export function ChatMessageBubble({ message, chatId }: ChatMessageBubbleProps) {
  const initial = useMemo(() => getInitialMedia(message), [message]);
  const [mimetype, setMimetype] = useState(initial.mimetype);
  const [filename, setFilename] = useState(initial.filename);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [mediaLoading, setMediaLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const type = message._data?.type || message.type;
  const time = format(new Date(message.timestamp * 1000), "HH:mm");

  if (SYSTEM_MESSAGE_TYPES.includes(type)) {
    const isDisappearing =
      type === "notification_template" &&
      (message._data?.subtype || message.subtype) === "disappearing_mode_update";
    return (
      <div className="flex justify-center py-1">
        <span className="rounded-full bg-muted px-3 py-1 text-[11px] text-muted-foreground text-center max-w-[85%]">
          {isDisappearing
            ? "Se actualizó la configuración de mensajes temporales"
            : "Los mensajes están cifrados de extremo a extremo"}
        </span>
      </div>
    );
  }

  const mediaType = getMediaType(mimetype);
  const isFile = !!filename;
  const preview = blobUrl || initial.thumbnail;

  const downloadMedia = async () => {
    if (blobUrl || mediaLoading) return;
    setMediaLoading(true);
    try {
      const full = await whatsappService.getChatMessage(chatId, message.id);
      const url = full?.media?.url;
      if (!url) {
        const err = full?.media?.error;
        throw new Error(err?.message || err?.details?.split("\n")[0] || "El archivo ya no está disponible");
      }
      if (full.media?.mimetype) setMimetype(full.media.mimetype);
      if (full.media?.filename) setFilename(full.media.filename);
      const blob = await whatsappService.downloadMedia(url);
      setBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error("No se pudo descargar el archivo", {
        description: getWhatsAppErrorMessage(e, "Inténtalo nuevamente"),
      });
    } finally {
      setMediaLoading(false);
    }
  };

  const saveMedia = () => {
    if (!blobUrl) return;
    const ext = (mimetype || "").split("/")[1]?.split(";")[0] || "bin";
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || `${message.id}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadOverlay = !blobUrl && (
    <button
      type="button"
      onClick={downloadMedia}
      className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/30 transition-colors hover:bg-black/40"
      aria-label="Descargar"
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background/90 text-foreground shadow">
        {mediaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      </span>
    </button>
  );

  const renderMedia = () => {
    if (mediaLoading && !preview && mediaType !== "other") {
      return <Skeleton className="h-32 w-60 rounded-lg" />;
    }
    if (mediaType === "image" && !isFile && preview) {
      return (
        <div className="relative w-60 overflow-hidden rounded-lg">
          <img src={preview} alt="Imagen" className="block w-full h-auto" />
          {downloadOverlay}
        </div>
      );
    }
    if (mediaType === "video" && !isFile && preview) {
      return (
        <div className="relative w-60 overflow-hidden rounded-lg">
          <video
            src={blobUrl || undefined}
            poster={initial.thumbnail || undefined}
            controls={!!blobUrl}
            className="block w-full h-auto"
          />
          {downloadOverlay}
        </div>
      );
    }
    if (mediaType === "audio" && !isFile) {
      return blobUrl ? (
        <audio src={blobUrl} controls className="w-60 max-w-full" />
      ) : (
        <Button variant="outline" size="sm" onClick={downloadMedia} disabled={mediaLoading} className="bg-background/60">
          {mediaLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Music className="mr-2 h-4 w-4" />}
          Cargar audio
        </Button>
      );
    }
    return (
      <div className="flex items-center gap-3 rounded-lg border bg-background/60 p-2.5 w-60 max-w-full">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <FileText className="h-4 w-4" />
        </div>
        <span className="flex-1 truncate text-sm">{filename || "Archivo"}</span>
        {!blobUrl && (
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={downloadMedia} disabled={mediaLoading}>
            {mediaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          </Button>
        )}
      </div>
    );
  };

  const isUnsupported = !message.body && !message.hasMedia;

  return (
    <div className={cn("flex", message.fromMe ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[80%] rounded-2xl px-3 py-2 shadow-sm",
          message.fromMe
            ? "rounded-br-sm bg-primary/10 border border-primary/20"
            : "rounded-bl-sm bg-card border",
        )}
      >
        {!message.fromMe && message.participant && (
          <p className="mb-1 text-xs font-medium text-primary">{message.participant}</p>
        )}

        {isUnsupported && (
          <div className="flex items-start gap-2 text-sm text-amber-600 dark:text-amber-500">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Este tipo de mensaje no es compatible. Ábrelo en tu teléfono.</span>
          </div>
        )}

        {message.hasMedia && (
          <div className="mb-1 space-y-2">
            {renderMedia()}
            {blobUrl && (
              <button
                type="button"
                onClick={saveMedia}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <Save className="h-3 w-3" />
                Guardar
              </button>
            )}
            {!blobUrl && !preview && mediaType !== "audio" && mediaType !== "other" && (
              <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <Smartphone className="h-3 w-3" />
                Vista previa no disponible
              </p>
            )}
          </div>
        )}

        {message.body && (
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
            {message.body}
          </p>
        )}

        <div className="mt-1 flex items-center justify-end gap-1">
          <span className="text-[10px] text-muted-foreground">{time}</span>
          {message.fromMe && <MessageAck ack={message.ack} />}
        </div>
      </div>
    </div>
  );
}
